import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, Users, Building2, CreditCard,
  AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw,
  ArrowUpRight, ArrowDownRight, Wallet
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FinancialTabNav from '../components/FinancialTabNav';
import api from '../services/api';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const AdminFinancialOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/financial/overview');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = 'blue', trend }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 rounded-xl bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  if (loading) {
    return (
      <Sidebar>
        <FinancialTabNav />
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Sidebar>
    );
  }

  if (!data) return null;

  const { companies, revenue, metrics, stripe_balance, mrr_history } = data;
  const planEntries = Object.entries(revenue.by_plan || {});

  return (
    <Sidebar>
      <FinancialTabNav />
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Revenue Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="Monthly Recurring Revenue" value={`$${revenue.mrr.toLocaleString()}`} color="green" sub={`ARR: $${revenue.arr.toLocaleString()}`} />
            <StatCard icon={TrendingUp} label="Total MRR (incl. overages)" value={`$${revenue.total_mrr.toLocaleString()}`} color="blue" sub={`Overages: $${revenue.overage_estimate.toLocaleString()}`} />
            <StatCard icon={Wallet} label="Lifetime Revenue" value={`$${revenue.lifetime_total.toLocaleString()}`} color="purple" />
            {stripe_balance ? (
              <StatCard icon={CreditCard} label="Stripe Balance" value={`$${stripe_balance.available.toLocaleString()}`} color="green" sub={`Pending: $${stripe_balance.pending.toLocaleString()}`} />
            ) : (
              <StatCard icon={CreditCard} label="Stripe Balance" value="—" color="gray" sub="Not connected" />
            )}
          </div>

          {/* Company Status Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={Building2} label="Total Companies" value={companies.total} color="blue" />
            <StatCard icon={CheckCircle} label="Active" value={companies.active} color="green" />
            <StatCard icon={Clock} label="Trialing" value={companies.trialing} color="amber" />
            <StatCard icon={AlertTriangle} label="Past Due" value={companies.past_due} color="red" />
            <StatCard icon={XCircle} label="Cancelled" value={companies.cancelled} color="gray" />
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={TrendingUp} label="Trial→Paid Conversion" value={`${metrics.conversion_rate}%`} color="green" />
            <StatCard icon={ArrowDownRight} label="Monthly Churn" value={`${metrics.churn_rate}%`} color="red" />
            <StatCard icon={CheckCircle} label="Payments (30d)" value={metrics.recent_payments} color="green" />
            <StatCard icon={AlertTriangle} label="Failed Payments (30d)" value={metrics.failed_payments} color="red" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Plan</h3>
              {planEntries.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No active subscriptions yet</p>
              ) : (
                <div className="space-y-3">
                  {planEntries.map(([name, info], idx) => {
                    const maxMRR = Math.max(...planEntries.map(([, v]) => v.mrr)) || 1;
                    const pct = (info.mrr / maxMRR) * 100;
                    return (
                      <div key={name}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400">{info.count} subscribers</span>
                            <span className="text-sm font-semibold text-gray-900">${info.mrr}/mo</span>
                          </div>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Subscriber Trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscriber Growth (6 months)</h3>
              {mrr_history && mrr_history.length > 0 ? (
                <div className="space-y-2">
                  {mrr_history.map((point, idx) => {
                    const maxSubs = Math.max(...mrr_history.map(p => p.subscribers)) || 1;
                    const pct = (point.subscribers / maxSubs) * 100;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-16 flex-shrink-0">{point.month}</span>
                        <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 8)}%` }}
                          >
                            <span className="text-xs text-white font-medium">{point.subscribers}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">No data yet</p>
              )}
            </div>
          </div>

          {/* Company Breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Health</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{companies.cc_valid}</p>
                <p className="text-sm text-gray-500 mt-1">Valid CC on File</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{companies.paid}</p>
                <p className="text-sm text-gray-500 mt-1">Paid Companies</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">{companies.grace_period}</p>
                <p className="text-sm text-gray-500 mt-1">In Grace Period</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-400">{companies.free_lifetime}</p>
                <p className="text-sm text-gray-500 mt-1">Free Lifetime</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminFinancialOverview;
